import { Input } from '@ds';
import { Controller, useFormContext } from 'react-hook-form';
import { CSSchedulerSettingsInputsFormType } from '@lp/features/Scheduler/SchedulerClinicalSpecialistsSettings/types';
import { getNumberInputValue, getNumberInputValueHandler } from '@lp/features/Scheduler/utils';
import { OrganType, OrganTypeWithoutKidney } from '@/api/apis/case';

type CSClinicalLeadsFormSectionProps = {
  isReadOnly: boolean;
};

// TMDXLP-14091: Kidneys hidden from CS Settings Clinical Leads table. Revert OrganTypeWithoutKidney -> OrganType to restore.
const organList = Object.values(OrganTypeWithoutKidney) as OrganType[];

export const CSClinicalLeadsFormSection = ({ isReadOnly }: CSClinicalLeadsFormSectionProps) => {
  const { control } = useFormContext<CSSchedulerSettingsInputsFormType>();

  const renderTableRow = () => {
    return organList.map((organ) => (
      <td className="py-3 pl-5 w-25" key={`${organ}`}>
        <Controller
          name={`exactlyDailyPhoneSupport.${OrganType[organ]}`}
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <Input
              type="number"
              min={0}
              aria-label={`exactlyDailyPhoneSupport.${OrganType[organ]}`}
              isDisabled={isReadOnly}
              isInvalid={invalid}
              errorMessage={error?.message}
              value={getNumberInputValue(value)}
              onValueChange={getNumberInputValueHandler(onChange)}
              {...field}
            />
          )}
        />
      </td>
    ));
  };

  return (
    <>
      <div className="flex-1 w-full">
        <h2 className="text-hm text-[#111] mb-3">Clinical Leads Everyday</h2>
        <table
          aria-label="Schedule table"
          className="relative border-separate border-spacing-0 px-4 py-4 bg-white rounded-lg w-full"
        >
          <thead className="bg-default-100">
            <tr>
              <th className="rounded-s-lg" aria-label="Placeholder" />
              {organList.map((organ) => (
                <th
                  key={organ}
                  className="text-right text-foreground-500 text-tiny font-semibold px-3 py-3 last-of-type:rounded-e-lg"
                >
                  {organ}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td key="nationwide">
                <span className="text-sm font-bold">Nationwide</span>
              </td>
              {renderTableRow()}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="flex-1 ml-5">
        <ul className="list-disc ml-3">
          <li className="mb-3">
            <span className="text-sm font-bold">Strict Value: </span>
            <span className="text-sm">
              {`The algorithm will achieve the entered value. However, setting it too high may result in the algorithm
              being unable to generate a schedule due to staffing limitations. This value helps reduce the algorithm's
              processing time by narrowing the focus.`}
            </span>
          </li>
          <li>
            <span className="text-sm font-bold">Flex Value: </span>
            <span className="text-sm">
              {`This value represents a desired goal. The algorithm will attempt to approximate this target when
              constructing the schedule, although achieving it is not guaranteed. This value will help the algorithm
              build an optimized schedule.`}
            </span>
          </li>
        </ul>
      </div>
    </>
  );
};
